import * as React from "react"
import {
  type SortDirection,
  type TransactionColumnName,
} from "@digits-graphql/frontend/graphql-bearer"
import type { TransactionSortKeys } from "src/frontend/components/OS/Applications/Shared/Transactions/TransactionSort"
import {
  buildDisplayOptionAccessor,
  buildDisplayOptionDefaultAccessor,
  buildSortOptionsAccessors,
  type DisplayOption,
  type DisplayOptionAccessor,
  type DisplayOptionGroupContextProps,
  useBuildDisplayOptionGroup,
} from "src/frontend/components/OS/Shared/DisplayOptions/DisplayOption"
import {
  CATEGORY_ID_OPTION,
  DIMENSION_SEARCH_TERM_OPTION,
  DIMENSION_SORT_DIRECTION_OPTION,
  MAX_VALUE_OPTION,
  MIN_VALUE_OPTION,
  MISSING_PARTY_OPTION,
  OTHER_SIDE_CATEGORY_ID,
  OWNER_ID_OPTION,
  PARTY_ID_OPTION,
  TRANSACTION_COLUMN_NAME_SORT_BY_OPTION,
  TRANSACTION_SORT_OPTION,
  WITHOUT_CATEGORY_ANCESTORS_OPTION,
  WITHOUT_OTHER_SIDE_CATEGORY_ANCESTORS_OPTION,
} from "src/frontend/components/OS/Shared/DisplayOptions/DisplayOptionsAccessors"

/*
  CORE DISPLAY OPTION GROUP
*/

// Sort Option Group
type TransactionsDisplaySortOptions = {
  sortBy: DisplayOptionAccessor<TransactionColumnName>
  sortDirection: DisplayOptionAccessor<SortDirection>
}

// Filter Option Group
type TransactionsDisplayFilterOptions = {
  filterTerm: DisplayOptionAccessor<string>
  partyId: DisplayOptionAccessor<string>
  missingParty: DisplayOptionAccessor<boolean>
  minValue: DisplayOptionAccessor<number | undefined>
  maxValue: DisplayOptionAccessor<number | undefined>
  categoryId: DisplayOptionAccessor<string>
  withoutCategoryAncestors: DisplayOptionAccessor<boolean>
  ownerId: DisplayOptionAccessor<string>
  otherSideCategoryId: DisplayOptionAccessor<string>
  withoutOtherSideCategoryAncestors: DisplayOptionAccessor<boolean>
}

type TransactionsDisplayOptionGroup = TransactionsDisplaySortOptions &
  TransactionsDisplayFilterOptions

type TransactionsDisplayOptionsContextProps =
  DisplayOptionGroupContextProps<TransactionsDisplayOptionGroup> & {
    clearOnlyFilters: () => void
    currentFilterURLKeysCount: number
  }

/*
  DEFAULT DISPLAY OPTION GROUP
*/

const DEFAULT_TRANSACTIONS_DISPLAY_SORT_OPTIONS: TransactionsDisplaySortOptions = {
  sortBy: buildDisplayOptionDefaultAccessor(TRANSACTION_COLUMN_NAME_SORT_BY_OPTION),
  sortDirection: buildDisplayOptionDefaultAccessor(DIMENSION_SORT_DIRECTION_OPTION),
}

const DEFAULT_TRANSACTIONS_DISPLAY_FILTER_OPTIONS: TransactionsDisplayFilterOptions = {
  filterTerm: buildDisplayOptionDefaultAccessor(DIMENSION_SEARCH_TERM_OPTION),
  partyId: buildDisplayOptionDefaultAccessor(PARTY_ID_OPTION),
  missingParty: buildDisplayOptionDefaultAccessor(MISSING_PARTY_OPTION),
  minValue: buildDisplayOptionDefaultAccessor(MIN_VALUE_OPTION),
  maxValue: buildDisplayOptionDefaultAccessor(MAX_VALUE_OPTION),
  categoryId: buildDisplayOptionDefaultAccessor(CATEGORY_ID_OPTION),
  withoutCategoryAncestors: buildDisplayOptionDefaultAccessor(WITHOUT_CATEGORY_ANCESTORS_OPTION),
  otherSideCategoryId: buildDisplayOptionDefaultAccessor(OTHER_SIDE_CATEGORY_ID),
  withoutOtherSideCategoryAncestors: buildDisplayOptionDefaultAccessor(
    WITHOUT_OTHER_SIDE_CATEGORY_ANCESTORS_OPTION
  ),
  ownerId: buildDisplayOptionDefaultAccessor(OWNER_ID_OPTION),
}

const DEFAULT_TRANSACTIONS_DISPLAY_FILTER_URL_KEYS = Object.values(
  DEFAULT_TRANSACTIONS_DISPLAY_FILTER_OPTIONS
).map(({ option }) => option().urlKey)

const DEFAULT_TRANSACTIONS_DISPLAY_OPTION_GROUP: TransactionsDisplayOptionGroup = {
  ...DEFAULT_TRANSACTIONS_DISPLAY_SORT_OPTIONS,
  ...DEFAULT_TRANSACTIONS_DISPLAY_FILTER_OPTIONS,
}

/*
  CONTEXT
*/

export const TransactionsDisplayOptionsContext =
  React.createContext<TransactionsDisplayOptionsContextProps>({
    clearOptions: () => {},
    clearOnlyFilters: () => {},
    currentURLKeys: [],
    currentFilterURLKeysCount: 0,
    ...DEFAULT_TRANSACTIONS_DISPLAY_OPTION_GROUP,
  })

/*
  HOOKS
*/

export function useTransactionsDisplayOptions() {
  return React.useContext(TransactionsDisplayOptionsContext)
}

export function useBuildTransactionsDisplayOptionsContext(isActive: boolean) {
  const { getOption, setOption, clearOptions, currentURLKeys } = useBuildDisplayOptionGroup(
    DEFAULT_TRANSACTIONS_DISPLAY_OPTION_GROUP,
    isActive
  )

  const buildAccessor = React.useCallback(
    <T>(displayOption: DisplayOption<T>) =>
      buildDisplayOptionAccessor(displayOption, getOption, setOption, clearOptions),
    [clearOptions, getOption, setOption]
  )

  const buildSortAccessor = React.useCallback(
    <T extends TransactionSortKeys>(displayOption: DisplayOption<T | undefined>) =>
      buildSortOptionsAccessors(displayOption, getOption, setOption, clearOptions),
    [clearOptions, getOption, setOption]
  )

  const currentFilterURLKeysCount = React.useMemo(
    () =>
      currentURLKeys.reduce(
        (acc, urlKey: keyof TransactionsDisplayFilterOptions) =>
          acc + (DEFAULT_TRANSACTIONS_DISPLAY_FILTER_URL_KEYS.includes(urlKey) ? 1 : 0),
        0
      ),
    [currentURLKeys]
  )

  return React.useMemo((): TransactionsDisplayOptionsContextProps => {
    const { sortBy, sortDirection } = buildSortAccessor(TRANSACTION_SORT_OPTION)

    return {
      clearOptions,
      clearOnlyFilters: () => clearOptions(DEFAULT_TRANSACTIONS_DISPLAY_FILTER_URL_KEYS),

      currentURLKeys,
      currentFilterURLKeysCount,

      sortBy,
      sortDirection,

      filterTerm: buildAccessor(DIMENSION_SEARCH_TERM_OPTION),
      partyId: buildAccessor(PARTY_ID_OPTION),
      missingParty: buildAccessor(MISSING_PARTY_OPTION),
      minValue: buildAccessor(MIN_VALUE_OPTION),
      maxValue: buildAccessor(MAX_VALUE_OPTION),
      categoryId: buildAccessor(CATEGORY_ID_OPTION),

      withoutCategoryAncestors: buildAccessor(WITHOUT_CATEGORY_ANCESTORS_OPTION),
      otherSideCategoryId: buildAccessor(OTHER_SIDE_CATEGORY_ID),
      withoutOtherSideCategoryAncestors: buildAccessor(
        WITHOUT_OTHER_SIDE_CATEGORY_ANCESTORS_OPTION
      ),
      ownerId: buildAccessor(OWNER_ID_OPTION),
    }
  }, [buildAccessor, buildSortAccessor, clearOptions, currentFilterURLKeysCount, currentURLKeys])
}
